import { DestroyRef, inject, Injectable, Injector } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { fromEvent } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { MOBILE_PORTRAIT_QUERY } from 'src/app/shared/constants/responsive-breakpoints';
import {
  FirstVisitHintIds,
  FirstVisitHintService,
} from 'src/app/shared/services/first-visit-hint.service';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { MainTableRotationDialogComponent } from '../main-table-rotation-dialog/main-table-rotation-dialog.component';

const LEGACY_ROTATION_HINT_STORAGE_KEY = 'due-list-rotation-hint-seen';

@Injectable({ providedIn: 'root' })
export class TableRotationHintService {
  private readonly dialog = inject(MatDialog);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly firstVisitHint = inject(FirstVisitHintService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private dialogOpen = false;
  private watching = false;
  private registrationCount = 0;

  showIfNeeded(): void {
    this.migrateLegacyHintIfNeeded();

    if (!this.isPortrait()) {
      return;
    }

    if (this.registrationCount === 0) {
      this.firstVisitHint.registerHint(FirstVisitHintIds.tableRotation);
    }
    this.registrationCount += 1;
    this.ensureWatching();
  }

  release(): void {
    if (this.registrationCount === 0) {
      return;
    }

    this.registrationCount -= 1;
    if (this.registrationCount > 0) {
      return;
    }

    this.firstVisitHint.unregisterHint(FirstVisitHintIds.tableRotation);
  }

  private ensureWatching(): void {
    if (this.watching) {
      return;
    }
    this.watching = true;

    toObservable(this.firstVisitHint.activeHintId, { injector: this.injector })
      .pipe(
        filter((hintId) => hintId === FirstVisitHintIds.tableRotation),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        // Open after the current CD pass so MatDialog content (icons/translate) initializes cleanly.
        queueMicrotask(() => this.openDialog());
      });

    const portraitQuery = window.matchMedia(MOBILE_PORTRAIT_QUERY);
    fromEvent<MediaQueryListEvent>(portraitQuery, 'change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.matches) {
          this.syncRegistrationWithOrientation();
          return;
        }
        this.firstVisitHint.unregisterHint(FirstVisitHintIds.tableRotation);
      });
  }

  private openDialog(): void {
    if (this.dialogOpen || !this.firstVisitHint.shouldShowHint(FirstVisitHintIds.tableRotation)) {
      return;
    }

    if (!this.isPortrait()) {
      this.firstVisitHint.unregisterHint(FirstVisitHintIds.tableRotation);
      return;
    }

    this.dialogOpen = true;
    this.dialog
      .open(MainTableRotationDialogComponent, {
        disableClose: true,
        width: '20rem',
        maxWidth: 'calc(100vw - 2rem)',
        panelClass: 'main-table-rotation-dialog',
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed: boolean | undefined) => {
        this.dialogOpen = false;
        if (confirmed) {
          this.firstVisitHint.dismissHint(FirstVisitHintIds.tableRotation);
          this.registrationCount = 0;
          return;
        }
        this.release();
      });
  }

  private syncRegistrationWithOrientation(): void {
    if (this.registrationCount === 0 || !this.isPortrait()) {
      return;
    }

    this.firstVisitHint.registerHint(FirstVisitHintIds.tableRotation);
  }

  private isPortrait(): boolean {
    return window.matchMedia(MOBILE_PORTRAIT_QUERY).matches;
  }

  private migrateLegacyHintIfNeeded(): void {
    const userScopedKey = this.getUserScopedRotationKey();
    if (!userScopedKey || this.localStorageService.getSavedState(userScopedKey) === true) {
      return;
    }

    const legacyHintSeen =
      this.localStorageService.getSavedState(FirstVisitHintIds.tableRotation) === true ||
      this.localStorageService.getSavedState(LEGACY_ROTATION_HINT_STORAGE_KEY) === true;
    if (!legacyHintSeen) {
      return;
    }

    this.localStorageService.setSavedState(true, userScopedKey);
    this.localStorageService.removeSavedState(FirstVisitHintIds.tableRotation);
    this.localStorageService.removeSavedState(LEGACY_ROTATION_HINT_STORAGE_KEY);
  }

  private getUserScopedRotationKey(): string | null {
    const userId = this.store.selectSnapshot(TenantUserAccessState.userAccess)?.id ?? null;
    return userId ? `${FirstVisitHintIds.tableRotation}_${userId}` : null;
  }
}
